<template>
  <div>
    <div
      v-if="isLoading"
      class="w-full h-64 flex items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div v-else>
      <div
        v-if="breadcrumbs.length > 0"
        class="mb-4 flex items-center gap-2 text-sm"
      >
        <span
          class="text-primary-600 hover:text-primary-800 cursor-pointer"
          @click="navigateToFolder(null)"
        >
          Folders
        </span>
        <template
          v-for="(crumb, index) in breadcrumbs"
          :key="crumb.id"
        >
          <span>/</span>
          <span
            v-if="index < breadcrumbs.length - 1"
            class="text-primary-600 hover:text-primary-800 cursor-pointer"
            @click="navigateToFolderById(crumb.id)"
          >
            {{ crumb.name }}
          </span>
          <span
            v-else
            class="text-gray-600"
          >{{ crumb.name }}</span>
        </template>
      </div>

      <div
        v-if="currentFolders.length === 0 && currentFiles.length === 0"
        class="text-center py-8 text-gray-500"
      >
        No items found in this folder.
      </div>

      <div
        v-else
        class="grid grid-cols-1 gap-6"
      >
        <FolderTile
          v-for="folder in currentFolders"
          :key="folder.id"
          v-model="folderRefs[folder.id]"
          :business-id="businessId"
          display-style="cimStyle"
          @folder-click="handleFolderClick"
        />

        <FolderFileTile
          v-for="file in currentFiles"
          :key="file.id"
          v-model:file="fileRefs[file.id]"
          v-model:show-actions="showActions"
          display-style="cimStyle"
        >
          <template #actions>
            <Button
              size="small"
              severity="secondary"
              @click="viewFile(file)"
            >
              <Icon name="ic:outline-remove-red-eye" />
            </Button>
            <Button
              v-if="mode === 'MULTIPLE_SELECT'"
              size="small"
              :severity="isFileSelected(file) ? 'success' : 'primary'"
              @click="toggleFileSelection(file)"
            >
              {{ isFileSelected(file) ? 'Selected' : 'Select' }}
            </Button>
            <Button
              v-else
              size="small"
              severity="primary"
              @click="selectFile(file)"
            >
              Select
            </Button>
          </template>
        </FolderFileTile>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Folder {
  id: string
  name: string
  folderType: string
  parentFolderId?: string | null
  Files?: Array<{ id: string }>
  childFolders?: Folder[]
}

interface File {
  id: string
  name: string
  createdAt: string
  publicUrl: string
}

interface Breadcrumb {
  id: string
  name: string
}

const { getAllFolders, getFolderById } = useFolder()

interface FolderWithChildren extends Folder {
  childFolders?: Folder[]
  Files?: File[]
  parentFolder?: FolderWithChildren | null
}

interface FolderResponse {
  data: FolderWithChildren
}

const isLoading = ref(false)
const rootFolders = ref<Folder[]>([])
const activeFolder = ref<FolderWithChildren | null>(null)
const allFoldersCache = ref<Folder[]>([])
const breadcrumbs = ref<Breadcrumb[]>([])
const displayStyle = ref('default')
const showActions = ref(true)

const mode = defineModel<string>('mode', {
  type: String,
  required: false,
  default: 'SINGLE_SELECT',
})
const selectedFiles = defineModel<Array<File>>('selectedFiles', {
  type: Array,
  required: false,
  default: [],
})
const allFolders = defineModel<Array<Folder>>('allFolders', {
  type: Array,
  required: false,
  default: [],
})

const selectedFile = defineModel<File>('selectedFile')

const businessId = computed(() => {
  return useRoute().params.businessId.toString()
})

const currentFolders = computed(() => {
  if (!activeFolder.value) {
    return rootFolders.value
  }
  return activeFolder.value.childFolders || []
})

const currentFiles = computed(() => {
  if (!activeFolder.value) {
    return []
  }
  return activeFolder.value.Files || []
})

const folderRefs = computed(() => {
  const refs: Record<string, Folder> = {}

  rootFolders.value.forEach((folder) => {
    refs[folder.id] = folder
  })

  if (activeFolder.value?.childFolders) {
    activeFolder.value.childFolders.forEach((folder) => {
      refs[folder.id] = folder
    })
  }

  return refs
})

const fileRefs = computed(() => {
  const refs: Record<string, File> = {}
  if (currentFiles.value) {
    currentFiles.value.forEach((file) => {
      refs[file.id] = file
    })
  }
  return refs
})

// Load all folders and cache them for populating childFolders
const loadAllFoldersCache = async () => {
  if (allFoldersCache.value.length > 0) {
    return
  }

  const allFoldersRes = await getAllFolders(businessId.value, null)
  allFoldersCache.value = (allFoldersRes.data || []) as Folder[]
}

// Load root folders
const loadRootFolders = async () => {
  isLoading.value = true
  try {
    await loadAllFoldersCache()
    rootFolders.value = allFoldersCache.value

    if (allFolders.value) {
      allFolders.value = rootFolders.value
    }
  }
  catch (error) {
    console.error('Error loading root folders:', error)
    rootFolders.value = []
  }
  finally {
    isLoading.value = false
  }
}

const loadFolder = async (folderId: string) => {
  isLoading.value = true
  try {
    await loadAllFoldersCache()

    const res = await getFolderById(folderId) as FolderResponse
    activeFolder.value = res.data

    if (activeFolder.value?.childFolders) {
      activeFolder.value.childFolders.forEach((childFolder) => {
        childFolder.childFolders = allFoldersCache.value.filter(
          f => f.parentFolderId === childFolder.id,
        )
      })
    }

    const crumbs: Breadcrumb[] = []
    let folder: FolderWithChildren | null = activeFolder.value
    while (folder) {
      crumbs.unshift({ id: folder.id, name: folder.name })
      if (folder.parentFolder) {
        folder = folder.parentFolder
      }
      else {
        break
      }
    }
    breadcrumbs.value = crumbs
  }
  catch (error) {
    console.error('Error loading folder:', error)
    activeFolder.value = null
  }
  finally {
    isLoading.value = false
  }
}

const handleFolderClick = (folder: Folder) => {
  navigateToFolder(folder)
}

const navigateToFolder = async (folder: Folder | null) => {
  if (!folder) {
    activeFolder.value = null
    breadcrumbs.value = []
    await loadRootFolders()
    return
  }

  await loadFolder(folder.id)
}

const navigateToFolderById = async (folderId: string) => {
  await loadFolder(folderId)
}

const viewFile = (file: File) => {
  window.open(file.publicUrl, '_blank')
}

const selectFile = (file: File) => {
  if (mode.value === 'SINGLE_SELECT') {
    selectedFile.value = file
  }
  else {
    const _selectedFiles = [...selectedFiles.value]
    if (!_selectedFiles.find(x => x.id === file.id)) {
      _selectedFiles.push(file)
    }
    selectedFiles.value = _selectedFiles
  }
}

const isFileSelected = (file: File) => {
  return selectedFiles.value.some(x => x.id === file.id)
}

const toggleFileSelection = (file: File) => {
  if (isFileSelected(file)) {
    removeFileFromSelection(file)
  }
  else {
    selectFile(file)
  }
}

const removeFileFromSelection = (file: File) => {
  const _selectedFiles = selectedFiles.value.filter(x => x.id !== file.id)
  selectedFiles.value = _selectedFiles
}

onMounted(() => {
  loadRootFolders()
})

const resetSelectedFolder = () => {
  navigateToFolder(null)
}

defineExpose({
  resetSelectedFolder,
})
</script>
